import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from "@/hooks/use-api-keys";
import type { ApiKey } from "@/types";
import { Check, Copy, KeyRound, Plus, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export default function ApiKeysPage() {
  const { data, isLoading } = useApiKeys();
  const createMut = useCreateApiKey();
  const revokeMut = useRevokeApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = () => {
    if (!name.trim() || createMut.isPending) return;
    createMut.mutate(
      { name: name.trim() },
      {
        onSuccess: (data) => {
          setCreatedKey(data.api_key);
          setName("");
          toast.success("API key created");
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      }
    );
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreatedKey(null);
    setCopied(false);
    setName("");
  };

  const handleRevoke = (key: ApiKey) => {
    if (key.revoked) return;
    if (!window.confirm(`Revoke API key "${key.name}"? MCP clients using it will lose access immediately.`)) return;
    revokeMut.mutate(key.id, {
      onSuccess: () => toast.success("API key revoked"),
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  const columns: Column<ApiKey>[] = [
    { key: "name", header: "Name" },
    {
      key: "key_prefix",
      header: "Key",
      render: (k) => <span className="font-mono text-xs">{k.key_prefix}…</span>,
    },
    {
      key: "revoked",
      header: "Status",
      render: (k) =>
        k.revoked ? (
          <Badge variant="destructive">Revoked</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        ),
    },
    { key: "created_at", header: "Created", render: (k) => formatDate(k.created_at) },
    { key: "last_used_at", header: "Last used", render: (k) => formatDate(k.last_used_at) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">MCP API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Keys grant AI agents full access to the MCP server at <span className="font-mono">/mcp</span>.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="sm" className="gap-1.5">
          <Plus size={14} />
          Create Key
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        getRowKey={(k) => k.id}
        onDelete={handleRevoke}
        emptyMessage="No API keys yet. Create one to connect an MCP client."
      />

      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <KeyRound size={14} className="text-muted-foreground" />
            Connect an MCP client
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          <p className="text-sm text-muted-foreground">
            Claude Code:
          </p>
          <pre className="bg-muted rounded-md p-3 text-xs font-mono overflow-x-auto">
{`claude mcp add --transport http asterisk http://<host>:8080/mcp \\
  --header "Authorization: Bearer <api-key>"`}
          </pre>
          <p className="text-sm text-muted-foreground">
            Any streamable-HTTP MCP client works: URL <span className="font-mono">http://&lt;host&gt;:8080/mcp</span> with
            header <span className="font-mono">Authorization: Bearer &lt;api-key&gt;</span> (or{" "}
            <span className="font-mono">X-API-Key</span>).
          </p>
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onClose={closeCreate}
        title={createdKey ? "API Key Created" : "Create API Key"}
      >
        {createdKey ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
              <TriangleAlert size={16} className="mt-0.5 shrink-0 text-warning" />
              <span>
                Copy this key now — it is shown <strong>only once</strong> and cannot be
                retrieved later.
              </span>
            </div>
            <div className="flex gap-2">
              <Input readOnly value={createdKey} className="font-mono text-xs" />
              <Button onClick={handleCopy} size="sm" variant="outline" className="gap-1.5 shrink-0">
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button onClick={closeCreate} size="sm">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="claude-code"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                A label to identify who or what uses this key.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={closeCreate} size="sm" variant="outline">
                Cancel
              </Button>
              <Button onClick={handleCreate} size="sm" disabled={!name.trim() || createMut.isPending}>
                Create
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
